import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import AuthModal from "@/components/AuthModal";
import Nav from "@/components/landing/Nav";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import Manifesto from "@/components/landing/Manifesto";
import Songs from "@/components/landing/Songs";
import Stories from "@/components/landing/Stories";
import Grow from "@/components/landing/Grow";
import Rest from "@/components/landing/Rest";
import Community from "@/components/landing/Community";
import Activities from "@/components/landing/Activities";
import TryItNow from "@/components/landing/TryItNow";
import Shop from "@/components/landing/Shop";
import Faq from "@/components/landing/Faq";
import Footer from "@/components/landing/Footer";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Kids from "@/pages/Kids";
import SignIn from "@/pages/SignIn";
import Watch from "@/pages/Watch";
import AdminMedia from "@/pages/AdminMedia";
import ResetPassword from "@/pages/ResetPassword";
import PaymentResult from "@/pages/PaymentResult";
import FreePack from "@/pages/FreePack";
import PackView from "@/pages/PackView";
import Unsubscribed from "@/pages/Unsubscribed";
import Contact from "@/pages/Contact";
import MobileTabs from "@/components/MobileTabs";

const HUB_TILES = [
  { label: "Sing", to: "/sing", color: "#FFC107", blurb: "Songs to sing & dance to" },
  { label: "Stories", to: "/stories", color: "#16A34A", blurb: "Bible adventures" },
  { label: "Rest", to: "/rest", color: "#9333EA", blurb: "Bedtime & lullabies" },
  { label: "Community", to: "/community", color: "#0284C7", blurb: "Parent community" },
  { label: "Activities", to: "/activities", color: "#FF6B6B", blurb: "Print & play" },
  { label: "Clips", to: "/clips", color: "#0284C7", blurb: "1-minute videos" },
  { label: "Episodes", to: "/episodes", color: "#9333EA", blurb: "10-minute videos" },
  { label: "Shop", to: "/shop", color: "#16A34A", blurb: "Printables & goodies" },
];

const Home = () => {
  const { user } = useAuth();
  const signedIn = !!(user && user.user_id);
  return (
    <main className="overflow-x-clip" data-testid="home-page">
      <Nav />
      <Hero />
      <Marquee />
      {signedIn && (
        <section className="mx-auto max-w-6xl px-4 pb-28 sm:px-6" data-testid="home-hub">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Where to today?
          </h2>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {HUB_TILES.map((t) => (
              <a
                key={t.to}
                href={t.to}
                data-testid={`hub-${t.label.toLowerCase()}`}
                className="group rounded-3xl border border-[#E2DDD0] bg-white p-6 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)] transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-10px_rgba(14,165,233,0.2)]"
              >
                <span className="block h-3 w-10 rounded-full transition-all group-hover:w-16" style={{ backgroundColor: t.color }} />
                <span className="mt-4 block font-display text-lg font-bold text-slate-900">{t.label}</span>
                <span className="mt-1 block text-sm text-slate-500">{t.blurb}</span>
              </a>
            ))}
          </div>
        </section>
      )}
      <Footer />
      <MobileTabs />
    </main>
  );
};

const SectionPage = ({ children, testid }) => (
  <main className="overflow-x-clip" data-testid={testid}>
    <Nav />
    <div className="pt-16">{children}</div>
    <Footer />
    <MobileTabs />
  </main>
);

const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user === false) navigate("/signin", { replace: true });
  }, [user, navigate]);
  if (!user) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#FFFDF6]" data-testid="app-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#FCD34D] border-t-[#FFC107]" />
      </div>
    );
  }
  return children;
};

function AppRouter() {
  const location = useLocation(); // fragment read here is reactive — do not use window.location.hash
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/free-pack" element={<FreePack />} />
      <Route path="/free-pack/pack" element={<PackView />} />
      <Route path="/unsubscribed" element={<Unsubscribed />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/promise" element={<RequireAuth><SectionPage testid="page-promise"><Manifesto /></SectionPage></RequireAuth>} />
      <Route path="/sing" element={<SectionPage testid="page-sing"><Songs /></SectionPage>} />
      <Route path="/stories" element={<SectionPage testid="page-stories"><Stories /></SectionPage>} />
      <Route path="/grow" element={<RequireAuth><SectionPage testid="page-grow"><Grow /></SectionPage></RequireAuth>} />
      <Route path="/rest" element={<RequireAuth><SectionPage testid="page-rest"><Rest /></SectionPage></RequireAuth>} />
      <Route path="/community" element={<RequireAuth><SectionPage testid="page-community"><Community /></SectionPage></RequireAuth>} />
      <Route path="/activities" element={<RequireAuth><SectionPage testid="page-activities"><Activities /></SectionPage></RequireAuth>} />
      <Route path="/try" element={<RequireAuth><SectionPage testid="page-try"><TryItNow /></SectionPage></RequireAuth>} />
      <Route path="/shop" element={<SectionPage testid="page-shop"><Shop /></SectionPage>} />
      <Route path="/faq" element={<RequireAuth><SectionPage testid="page-faq"><Faq /></SectionPage></RequireAuth>} />
      <Route path="/clips" element={<Watch kind="clip" />} />
      <Route path="/episodes" element={<Watch kind="episode" />} />
      <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
      <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
      <Route path="/admin/media" element={<RequireAuth><AdminMedia /></RequireAuth>} />
      <Route path="/kids" element={<RequireAuth><Kids /></RequireAuth>} />
      <Route path="/payment/success" element={<RequireAuth><PaymentResult kind="success" /></RequireAuth>} />
      <Route path="/payment/cancel" element={<RequireAuth><PaymentResult kind="cancel" /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    const onAnchor = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (a) {
        const el = document.querySelector(a.getAttribute("href"));
        if (el) {
          e.preventDefault();
          lenis.scrollTo(el, { offset: -80 });
        }
      }
    };
    document.addEventListener("click", onAnchor);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      document.removeEventListener("click", onAnchor);
    };
  }, []);

  return (
    <div className="App">
      <div className="grain-overlay" aria-hidden="true" />
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <AuthModal />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
